import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  getCustomer,
  listCustomerInternalAccounts,
  listPlatformInternalAccounts,
  listCustomerExternalAccounts,
  listTransactions,
  deleteCustomer,
  approveTransaction,
  rejectTransaction,
  getConfig,
} from "../services/gridApi";
import { useDemo } from "../context/DemoContext";
import { formatCurrencyAmount as fmtCurrAmt } from "../data/mock";
import CreateExternalAccountModal from "../components/CreateExternalAccountModal";

function formatAmount(amount: number, currency: string): string {
  if (currency === "BTC") return (amount / 100_000_000).toFixed(8) + " BTC";
  return (amount / 100).toFixed(2) + " " + currency;
}

export default function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { demo, getDemoInternalAccounts, getDemoExternalAccounts, getDemoTransactions, getDemoCustomers, demoCreateInternalAccount } = useDemo();
  const [customer, setCustomer] = useState<any>(null);
  const [internalAccounts, setInternalAccounts] = useState<any[]>([]);
  const [platformInternalAccounts, setPlatformInternalAccounts] = useState<any[]>([]);
  const [externalAccounts, setExternalAccounts] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [platformConfig, setPlatformConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [iaDebug, setIaDebug] = useState<string>("");
  const [refreshingIA, setRefreshingIA] = useState(false);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError("");

    // Demo mode: use mock data
    if (demo.enabled) {
      const demoCustomers = getDemoCustomers();
      const cust = demoCustomers.find((c) => c.id === id);
      if (!cust) {
        setError("Customer not found in demo data");
        setLoading(false);
        return;
      }
      setCustomer(cust);
      setInternalAccounts(getDemoInternalAccounts(id));
      setPlatformInternalAccounts(getDemoInternalAccounts("PLATFORM"));
      setExternalAccounts(getDemoExternalAccounts(id));
      setTransactions(getDemoTransactions(id));
      setIaDebug(`Demo mode: ${getDemoInternalAccounts(id).length} customer accounts`);
      setLoading(false);
      return;
    }

    try {
      const [cust, ia, allIa, platformIa, ea, tx, config] = await Promise.all([
        getCustomer(id),
        listCustomerInternalAccounts({ customerId: id }).catch((err) => {
          console.warn("Customer internal accounts fetch failed:", err);
          setIaDebug(`Customer accounts error ${err.status}: ${err.message}`);
          return { data: [] };
        }),
        listCustomerInternalAccounts({}).catch(() => ({ data: [] })),
        listPlatformInternalAccounts({}).catch(() => ({ data: [] })),
        listCustomerExternalAccounts({ customerId: id }),
        listTransactions({ customerId: id, limit: 20 }),
        getConfig().catch(() => null),
      ]);
      setCustomer(cust);
      // Use customer-specific accounts, fall back to all customer accounts filtered, then platform accounts
      const custAccounts = ia.data || [];
      const allCustAccounts = allIa.data || [];
      const platAccounts = platformIa.data || [];
      setInternalAccounts(custAccounts);
      setPlatformInternalAccounts(platAccounts);
      setExternalAccounts(ea.data || []);
      setTransactions(tx.data || []);
      if (config) setPlatformConfig(config);

      const debugParts: string[] = [];
      debugParts.push(`Customer accounts: ${custAccounts.length}`);
      debugParts.push(`All customer accounts: ${allCustAccounts.length}`);
      debugParts.push(`Platform accounts: ${platAccounts.length}`);
      setIaDebug(debugParts.join(" | "));
    } catch (e: any) {
      setError(e.message || "Failed to load customer");
    } finally {
      setLoading(false);
    }
  }, [id, demo.enabled, getDemoCustomers, getDemoInternalAccounts, getDemoExternalAccounts, getDemoTransactions]);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-poll for internal accounts if empty (they may be created async)
  const pollCountRef = useRef(0);
  useEffect(() => {
    if (demo.enabled || loading || !id || internalAccounts.length > 0) {
      pollCountRef.current = 0;
      return;
    }
    if (pollCountRef.current >= 5) return; // stop after 5 retries

    const timer = setTimeout(async () => {
      pollCountRef.current += 1;
      console.log(`[Internal Accounts] Auto-poll attempt ${pollCountRef.current}/5 for ${id}`);
      try {
        const ia = await listCustomerInternalAccounts({ customerId: id });
        if (ia.data && ia.data.length > 0) {
          setInternalAccounts(ia.data);
          setIaDebug(`Found ${ia.data.length} account(s) after ${pollCountRef.current} poll(s)`);
        } else {
          setIaDebug(`Poll ${pollCountRef.current}/5: still empty`);
        }
      } catch {
        // ignore poll errors
      }
    }, 2000 * pollCountRef.current + 2000); // 2s, 4s, 6s, 8s, 10s

    return () => clearTimeout(timer);
  }, [loading, id, internalAccounts.length]);

  const refreshInternalAccounts = async () => {
    if (!id) return;
    setRefreshingIA(true);
    setIaDebug("");
    try {
      const ia = await listCustomerInternalAccounts({ customerId: id });
      setInternalAccounts(ia.data || []);
      setIaDebug(
        ia.data?.length
          ? `Found ${ia.data.length} account(s)`
          : "API returned empty array (0 accounts)"
      );
    } catch (err: any) {
      setIaDebug(`Error ${err.status}: ${err.message}`);
    } finally {
      setRefreshingIA(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await deleteCustomer(id);
      navigate("/customers");
    } catch (e: any) {
      setError(e.message);
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleApprove = async (txId: string) => {
    try {
      await approveTransaction(txId);
      loadData();
    } catch (e: any) {
      alert("Approve failed: " + e.message);
    }
  };

  const handleReject = async (txId: string) => {
    try {
      await rejectTransaction(txId);
      loadData();
    } catch (e: any) {
      alert("Reject failed: " + e.message);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-[#8A8A8A]">
        <div className="w-5 h-5 border-2 border-[#00B3E0] border-t-transparent rounded-full animate-spin" />
        Loading customer...
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="p-8">
        <div className="text-red-400 mb-2">{error || "Customer not found"}</div>
        <Link to="/customers" className="text-sm text-[#00B3E0] hover:text-[#00C4F5]">
          Back to customers
        </Link>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-[#8A8A8A] mb-6">
        <Link to="/customers" className="hover:text-[#CCCCCC]">Customers</Link>
        <span>/</span>
        <span className="text-[#F8F8F7]">{customer.fullName || customer.platformCustomerId || customer.id}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-[#F8F8F7] tracking-tight">
              {customer.fullName || customer.platformCustomerId || "Customer"}
            </h1>
            <span className={`text-xs px-2 py-1 rounded-full ${
              customer.kycStatus === "APPROVED" || customer.kycStatus === "MANUALLY_APPROVED"
                ? "bg-emerald-500/10 text-emerald-400"
                : customer.kycStatus === "PENDING_REVIEW"
                ? "bg-yellow-500/10 text-yellow-400"
                : "bg-[#222222] text-[#8A8A8A]"
            }`}>
              {customer.kycStatus || "N/A"}
            </span>
            <span className="text-xs px-2 py-1 rounded-full bg-[#222222] text-[#8A8A8A]">
              {customer.customerType}
            </span>
          </div>
          <div className="flex items-center gap-3 text-sm text-[#8A8A8A] mt-1">
            {customer.umaAddress && <span className="font-mono text-xs">{customer.umaAddress}</span>}
            {customer.email && <span>{customer.email}</span>}
          </div>
        </div>
        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="text-xs text-red-400 border border-red-400/20 px-3 py-2 rounded-lg hover:bg-red-500/10"
        >
          Delete
        </button>
      </div>

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[#111111] border border-[#222222] rounded-lg w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold text-[#F8F8F7] mb-2">Delete Customer?</h3>
            <p className="text-sm text-[#8A8A8A] mb-4">
              This calls DELETE /customers/{customer.id}. Cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 border border-[#333333] text-[#F8F8F7] py-2 rounded-lg hover:bg-[#222222]">
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleting} className="flex-1 bg-red-500 text-white py-2 rounded-lg hover:bg-red-400 disabled:opacity-50">
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Info */}
      <div className="bg-[#111111] border border-[#222222] rounded-lg p-5 mb-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <div className="text-xs text-[#8A8A8A] mb-1">Grid ID</div>
            <div className="text-sm text-[#CCCCCC] font-mono truncate">{customer.id}</div>
          </div>
          <div>
            <div className="text-xs text-[#8A8A8A] mb-1">Platform ID</div>
            <div className="text-sm text-[#CCCCCC] font-mono">{customer.platformCustomerId}</div>
          </div>
          <div>
            <div className="text-xs text-[#8A8A8A] mb-1">Created</div>
            <div className="text-sm text-[#CCCCCC]">{new Date(customer.createdAt).toLocaleDateString()}</div>
          </div>
          <div>
            <div className="text-xs text-[#8A8A8A] mb-1">Updated</div>
            <div className="text-sm text-[#CCCCCC]">{new Date(customer.updatedAt).toLocaleDateString()}</div>
          </div>
        </div>

        {/* Raw JSON toggle */}
        <details className="mt-4">
          <summary className="text-xs text-[#00B3E0] cursor-pointer">Show raw API response</summary>
          <pre className="mt-2 text-xs font-mono text-[#CCCCCC] bg-[#0A0A0A] p-3 rounded-lg overflow-auto max-h-48">
            {JSON.stringify(customer, null, 2)}
          </pre>
        </details>
      </div>

      {/* Internal Accounts */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-[#F8F8F7]">
            Internal Accounts ({internalAccounts.length + platformInternalAccounts.length})
          </h2>
          <div className="flex items-center gap-2">
            {demo.enabled && (
              <select
                onChange={(e) => {
                  if (e.target.value && id) {
                    const result = demoCreateInternalAccount({ customerId: id, currencyCode: e.target.value });
                    if (result.success) {
                      setInternalAccounts(getDemoInternalAccounts(id));
                      setIaDebug(`Provisioned ${e.target.value} account`);
                    } else {
                      setIaDebug(result.error || "Failed");
                    }
                    e.target.value = "";
                  }
                }}
                className="text-xs bg-[#0A0A0A] border border-[#00B3E0]/30 text-[#00B3E0] rounded-lg px-3 py-1.5 focus:outline-none"
                defaultValue=""
              >
                <option value="" disabled>+ Provision Account</option>
                {["USD", "EUR", "GBP", "BTC", "USDC", "BRL", "MXN", "CAD", "INR", "NGN"].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            )}
            {!demo.enabled && (
              <button
                onClick={refreshInternalAccounts}
                disabled={refreshingIA}
                className="text-xs text-[#00B3E0] border border-[#00B3E0]/20 px-3 py-1.5 rounded-lg hover:bg-[#00B3E0]/10 disabled:opacity-50 flex items-center gap-1.5"
              >
                {refreshingIA ? (
                  <div className="w-3 h-3 border-2 border-[#00B3E0] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182" />
                  </svg>
                )}
                Refresh
              </button>
            )}
          </div>
        </div>

        {/* Show all accounts (customer + platform) */}
        {(internalAccounts.length > 0 || platformInternalAccounts.length > 0) ? (
          <div className="space-y-5">
            {/* Customer Internal Accounts */}
            {internalAccounts.length > 0 && (
              <div>
                <div className="mb-3">
                  <h3 className="text-xs font-semibold text-[#8A8A8A] uppercase tracking-wider">Customer Wallets</h3>
                  <p className="text-[11px] text-[#666666] mt-0.5">Holds funds on behalf of this customer. Used for sending payouts and receiving payments.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {internalAccounts.map((acct) => (
                    <div key={acct.id} className="bg-[#111111] border border-emerald-500/20 rounded-lg p-5">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-[#F8F8F7]">
                          {acct.balance?.currency?.code || "?"} Account
                        </span>
                        <span className="text-[10px] text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">Customer</span>
                      </div>
                      <div className="text-xl font-semibold font-mono text-[#F8F8F7] mb-2">
                        {formatAmount(acct.balance?.amount || 0, acct.balance?.currency?.code || "USD")}
                      </div>
                      <div className="text-[11px] text-[#666666] font-mono truncate">{acct.id}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Platform Internal Accounts */}
            {platformInternalAccounts.length > 0 && (
              <div>
                <div className="mb-3">
                  <h3 className="text-xs font-semibold text-[#8A8A8A] uppercase tracking-wider">Platform Treasury</h3>
                  <p className="text-[11px] text-[#666666] mt-0.5">Shared platform-level accounts for liquidity, crypto operations, and cross-currency settlement.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {platformInternalAccounts.map((acct) => (
                    <div key={acct.id} className="bg-[#111111] border border-indigo-500/20 rounded-lg p-5">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-[#F8F8F7]">
                          {acct.balance?.currency?.code || "?"} Account
                        </span>
                        <span className="text-[10px] text-indigo-400 bg-indigo-500/10 px-1.5 py-0.5 rounded">Platform</span>
                      </div>
                      <div className="text-xl font-semibold font-mono text-[#F8F8F7] mb-2">
                        {formatAmount(acct.balance?.amount || 0, acct.balance?.currency?.code || "USD")}
                      </div>
                      <div className="text-[11px] text-[#666666] font-mono truncate">{acct.id}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-[#111111] border border-[#222222] rounded-lg p-5">
            <div className="text-sm text-[#888888] mb-2">No internal accounts yet</div>
            <div className="text-xs text-[#666666] mb-3">
              Internal accounts are created when the customer receives their first payment.
              Use <span className="text-[#00B3E0]">Simulator</span> to send a test payment to this customer.
            </div>
            <button
              onClick={() => navigate("/simulator")}
              className="text-xs bg-[#00B3E0] text-black font-semibold px-4 py-2 rounded-lg hover:bg-[#00C4F5]"
            >
              Go to Simulator
            </button>
          </div>
        )}
      </div>

      {/* External Accounts */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-[#F8F8F7]">
            External Accounts ({externalAccounts.length})
          </h2>
          <button
            onClick={() => setShowAddAccount(true)}
            className="text-xs bg-[#00B3E0] text-black font-semibold px-3 py-1.5 rounded-lg hover:bg-[#00C4F5]"
          >
            + Add Account
          </button>
        </div>
        {externalAccounts.length === 0 ? (
          <div className="bg-[#111111] border border-[#222222] rounded-lg p-8 text-center text-sm text-[#666666]">
            No external accounts linked.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {externalAccounts.map((acct) => {
              const acctType = acct.accountType || acct.accountInfo?.accountType || "Unknown";
              const currCode = typeof acct.currency === "object" ? acct.currency.code : acct.currency;
              return (
                <div key={acct.id} className="bg-[#111111] border border-[#222222] rounded-lg p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-[#F8F8F7]">
                      {acctType.replace(/_/g, " ")}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      acct.status === "ACTIVE" ? "bg-emerald-500/10 text-emerald-400" :
                      acct.status === "PENDING_VERIFICATION" || acct.status === "PENDING" ? "bg-yellow-500/10 text-yellow-400" :
                      "bg-[#222222] text-[#8A8A8A]"
                    }`}>
                      {acct.status}
                    </span>
                  </div>
                  <div className="text-xs text-[#8A8A8A]">{currCode}</div>
                  {acct.accountLabel && <div className="text-xs text-[#666666] mt-1">{acct.accountLabel}</div>}
                  <div className="text-[11px] text-[#666666] font-mono truncate mt-2">{acct.id}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Transactions */}
      <div className="bg-[#111111] border border-[#222222] rounded-lg overflow-hidden">
        <div className="p-5 border-b border-[#222222]">
          <h2 className="text-sm font-semibold text-[#F8F8F7]">
            Transactions ({transactions.length})
          </h2>
        </div>
        {transactions.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#666666]">No transactions yet</div>
        ) : (
          <div className="divide-y divide-[#222222]">
            {transactions.map((tx) => (
              <div key={tx.id} className="px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                    tx.type === "INCOMING" ? "bg-emerald-500/10 text-emerald-400" : "bg-[#00B3E0]/10 text-[#00B3E0]"
                  }`}>
                    {tx.type === "INCOMING" ? "IN" : "OUT"}
                  </div>
                  <div>
                    <div className="text-sm text-[#F8F8F7]">{tx.description || tx.id?.slice(0, 24)}</div>
                    <div className="text-xs text-[#666666]">{new Date(tx.createdAt).toLocaleString()}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    tx.status === "COMPLETED" ? "bg-emerald-500/10 text-emerald-400" :
                    tx.status === "PENDING" ? "bg-yellow-500/10 text-yellow-400" :
                    tx.status === "FAILED" ? "bg-red-500/10 text-red-400" :
                    "bg-[#222222] text-[#8A8A8A]"
                  }`}>
                    {tx.status}
                  </span>
                  {(tx.status === "PENDING" || tx.status === "CREATED") && tx.type === "INCOMING" && (
                    <div className="flex gap-1">
                      <button onClick={() => handleApprove(tx.id)} className="text-xs text-emerald-400 border border-emerald-400/20 px-2 py-1 rounded hover:bg-emerald-500/10">
                        Approve
                      </button>
                      <button onClick={() => handleReject(tx.id)} className="text-xs text-red-400 border border-red-400/20 px-2 py-1 rounded hover:bg-red-500/10">
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* External Account Modal */}
      <CreateExternalAccountModal
        isOpen={showAddAccount}
        onClose={() => setShowAddAccount(false)}
        customerId={customer.id}
        customerName={customer.fullName}
        onCreated={loadData}
      />
    </div>
  );
}
